/* API Layer — JWT token management + fetch helpers.
   Loaded before crm-data.jsx */

const API_BASE = '/api';

// ─── Token Management ───
const CrmAuth = {
  getToken: () => localStorage.getItem('crm_token'),
  setToken: (t) => localStorage.setItem('crm_token', t),
  clearToken: () => localStorage.removeItem('crm_token'),
  getUser: () => {
    try { return JSON.parse(localStorage.getItem('crm_user')); } catch { return null; }
  },
  setUser: (u) => localStorage.setItem('crm_user', JSON.stringify(u)),
  isLoggedIn: () => !!localStorage.getItem('crm_token'),
};

// ─── Fetch with auth ───
async function apiFetch(path, options = {}) {
  const token = CrmAuth.getToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401) {
    // Si es el endpoint de auth (login, forgot-password, etc.), NO recargar —
    // dejar que el formulario muestre el error inline.
    if (path.startsWith('/auth/')) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Credenciales inválidas');
    }
    CrmAuth.clearToken();
    window.location.reload();
    throw new Error('Sesión expirada');
  }

  if (res.status === 503) {
    const body = await res.json().catch(() => ({}));
    if (body.error === 'maintenance') {
      window.dispatchEvent(new Event('crm:maintenance'));
      throw new Error('maintenance');
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Error ${res.status}`);
  }

  return res.json();
}

// ─── API Methods ───
// Helper: convierte objeto de params a query string (?key=val&...)
const toQS = (params = {}) => {
  const entries = Object.entries(params || {}).filter(([, v]) => v != null && v !== '');
  if (!entries.length) return '';
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
};

const CrmApi = {
  // Auth
  login: (email, password, rememberMe = false) => apiFetch('/auth/login', {
    method: 'POST', body: JSON.stringify({ email, password, rememberMe })
  }),
  // Solo deja el cierre en los registros — el token lo borra el navegador.
  logout: () => apiFetch('/auth/logout', { method: 'POST' }),
  forgotPassword: (email) => apiFetch('/auth/forgot-password', {
    method: 'POST', body: JSON.stringify({ email })
  }),
  resetPassword: (token, password) => apiFetch('/auth/reset-password', {
    method: 'POST', body: JSON.stringify({ token, password })
  }),
  register: (data) => apiFetch('/auth/register', {
    method: 'POST', body: JSON.stringify({
      name: data.name, lastName: data.lastName, email: data.email,
      password: data.pass, phone: data.phone, dni: data.dni, cuit: data.cuit || undefined,
    })
  }),

  // Users (admin)
  getUsersFull: () => apiFetch('/users'),
  getPendingUsers: () => apiFetch('/users/pending'),
  createUser: (data) => apiFetch('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => apiFetch(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  toggleUser: (id) => apiFetch(`/users/${id}/toggle`, { method: 'PATCH' }),
  resendWelcome: (id) => apiFetch(`/users/${id}/resend-welcome`, { method: 'POST' }),
  approveUser: (id, role) => apiFetch(`/users/${id}/approve`, { method: 'POST', body: JSON.stringify({ role }) }),
  rejectUser: (id) => apiFetch(`/users/${id}/reject`, { method: 'POST' }),
  changeUserPassword: (id, password, currentPassword) => apiFetch(`/users/${id}/password`, {
    method: 'PATCH', body: JSON.stringify({ password, currentPassword })
  }),
  updateProfile: (id, data) => apiFetch(`/users/${id}/profile`, {
    method: 'PATCH', body: JSON.stringify(data)
  }),
  deleteAvatar: (id) => apiFetch(`/users/${id}/avatar`, { method: 'DELETE' }),
  uploadAvatar: async (id, file) => {
    const form = new FormData();
    form.append('avatar', file);
    const token = localStorage.getItem('crm_token');
    const res = await fetch(`/api/users/${id}/avatar`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Error al subir foto'); }
    return res.json();
  },

  // Quotes
  getQuotes: (p) => apiFetch(`/quotes${toQS(p)}`),
  parsePresupuesto: (file) => {
    const token = CrmAuth.getToken();
    const fd = new FormData(); fd.append('file', file);
    return fetch(`${API_BASE}/quotes/parse-presupuesto`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
    }).then(r => r.ok ? r.json() : r.json().then(b => Promise.reject(new Error(b.error || `Error ${r.status}`))));
  },
  createQuote: (data) => apiFetch('/quotes', { method: 'POST', body: JSON.stringify(data) }),
  updateQuotePedido: (id, pedido, pedidoDe, pedidoRef) =>
    apiFetch(`/quotes/${id}/pedido`, { method: 'PATCH', body: JSON.stringify({ pedido, pedidoDe, pedidoRef }) }),
  changeQuoteStage: (id, stage, extra = {}) => apiFetch(`/quotes/${id}/stage`, {
    method: 'PATCH', body: JSON.stringify({ stage, ...extra })
  }),
  assignQuote: (id, sellerId) => apiFetch(`/quotes/${id}/assign`, {
    method: 'PATCH', body: JSON.stringify({ sellerId })
  }),
  assignQuoteClient: (id, data) => apiFetch(`/quotes/${id}/client`, {
    method: 'PATCH', body: JSON.stringify(data)
  }),
  getQuoteDetail: (id) => apiFetch(`/quotes/${id}/detail`),
  duplicateQuote: (id, data) => apiFetch(`/quotes/${id}/duplicate`, {
    method: 'POST', body: JSON.stringify(data)
  }),
  deleteQuote: (id) => apiFetch(`/quotes/${id}`, { method: 'DELETE' }),
  addQuoteNote: (id, text, mentionedUserIds) => apiFetch(`/quotes/${id}/notes`, {
    method: 'POST', body: JSON.stringify({ text, mentionedUserIds })
  }),
  updateQuoteItem: (quoteId, itemId, data) => apiFetch(`/quotes/${quoteId}/items/${itemId}`, {
    method: 'PATCH', body: JSON.stringify(data)
  }),
  createQuoteItem: (quoteId, data) => apiFetch(`/quotes/${quoteId}/items`, {
    method: 'POST', body: JSON.stringify(data)
  }),
  deleteQuoteItem: (quoteId, itemId) => apiFetch(`/quotes/${quoteId}/items/${itemId}`, {
    method: 'DELETE'
  }),
  updateQuoteAmount: (id, amount) => apiFetch(`/quotes/${id}/amount`, {
    method: 'PATCH', body: JSON.stringify({ amount })
  }),
  updateQuoteDeadline: (id, deadline) => apiFetch(`/quotes/${id}/deadline`, {
    method: 'PATCH', body: JSON.stringify({ deadline })
  }),
  updateQuotePriority: (id, priority) => apiFetch(`/quotes/${id}/priority`, {
    method: 'PATCH', body: JSON.stringify({ priority })
  }),
  revisarQuote: (id, motivo) => apiFetch(`/quotes/${id}/revisar`, {
    method: 'POST', body: JSON.stringify({ motivo })
  }),
  reactivarQuote: (id) => apiFetch(`/quotes/${id}/reactivar`, { method: 'POST' }),
  getPriorityQuotes: (filters = {}) => {
    const qs = new URLSearchParams();
    if (filters.sellerId) qs.set('sellerId', filters.sellerId);
    const s = qs.toString();
    return apiFetch(`/data/priority${s ? '?' + s : ''}`);
  },
  createNP: (formData) => {
    const token = CrmAuth.getToken();
    return fetch(`${API_BASE}/quotes/create-np`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    }).then(r => r.ok ? r.json() : r.json().then(b => Promise.reject(new Error(b.error || `Error ${r.status}`))));
  },
  linkQuote: (id, linkedQuoteId) => apiFetch(`/quotes/${id}/link`, {
    method: 'PATCH', body: JSON.stringify({ linkedQuoteId })
  }),

  // Clients
  getClients: () => apiFetch('/clients'),
  createClient: (data) => apiFetch('/clients', { method: 'POST', body: JSON.stringify(data) }),
  updateClient: (id, data) => apiFetch(`/clients/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getClientEmails: (id) => apiFetch(`/clients/${id}/emails`),
  addClientEmail: (id, email) => apiFetch(`/clients/${id}/emails`, {
    method: 'POST', body: JSON.stringify({ email })
  }),
  removeClientEmail: (id, emailId) => apiFetch(`/clients/${id}/emails/${emailId}`, { method: 'DELETE' }),
  deleteClient: (id) => apiFetch(`/clients/${id}`, { method: 'DELETE' }),
  getDeleteAllPreview: () => apiFetch('/clients/delete-all-preview'),
  deleteAllClients: (forceHistory = false) => apiFetch('/clients/all', {
    method: 'DELETE', body: JSON.stringify({ forceHistory }),
  }),
  exportClients: () => {
    const token = CrmAuth.getToken();
    return fetch(`${API_BASE}/clients/export`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async r => {
      if (!r.ok) { const b = await r.json(); throw new Error(b.error || `Error ${r.status}`); }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clientes-${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    });
  },
  previewClientsXLS: (file) => {
    const token = CrmAuth.getToken();
    const fd = new FormData(); fd.append('file', file);
    return fetch(`${API_BASE}/clients/preview`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
    }).then(r => r.ok ? r.json() : r.json().then(b => Promise.reject(new Error(b.error || `Error ${r.status}`))));
  },
  syncClients: (token, deleteCodes) => apiFetch('/clients/sync', {
    method: 'POST', body: JSON.stringify({ token, deleteCodes }),
  }),
  getLegacySellerGroups: () => apiFetch('/clients/legacy-seller-groups'),
  bulkAssignSeller: (legacySellerName, sellerId) => apiFetch('/clients/bulk-assign-seller', {
    method: 'POST', body: JSON.stringify({ legacySellerName, sellerId }),
  }),

  // Admin — reparseo de PDFs Flexxus
  getStorageReport: () => apiFetch('/admin/storage-report'),
  getRespaldos: () => apiFetch('/admin/respaldos'),
  respaldarAdjuntos: () => apiFetch('/admin/respaldos/adjuntos', { method: 'POST' }),
  getReparseCandidates: () => apiFetch('/admin/reparse-candidates'),
  reparsePreview: (quoteIds) => apiFetch('/admin/reparse-preview', {
    method: 'POST', body: JSON.stringify({ quoteIds }),
  }),
  reparseApply: (token) => apiFetch('/admin/reparse-apply', {
    method: 'POST', body: JSON.stringify({ token }),
  }),

  // Data
  getUsers: () => apiFetch('/data/users'),
  getStages: () => apiFetch('/data/stages'),
  getStagesFull: () => apiFetch('/data/stages/full'),
  updateStage: (id, data) => apiFetch(`/data/stages/${id}`, {
    method: 'PATCH', body: JSON.stringify(data)
  }),
  createStage: (data) => apiFetch('/data/stages', {
    method: 'POST', body: JSON.stringify(data)
  }),
  deleteStage: (id) => apiFetch(`/data/stages/${id}`, { method: 'DELETE' }),
  reorderStages: (ids) => apiFetch('/data/stages-reorder', {
    method: 'PATCH', body: JSON.stringify({ ids })
  }),
  getActivity: (limit = 20) => apiFetch(`/data/activity?limit=${limit}`),
  sync: (since) => apiFetch(`/data/sync?since=${encodeURIComponent(since)}`),
  getDashboard:    (p) => apiFetch(`/data/dashboard${toQS(p)}`),
  getChartSellers:    (p) => apiFetch(`/data/charts/sellers${toQS(p)}`),
  getChartStages:     (p) => apiFetch(`/data/charts/stages${toQS(p)}`),
  getChartMonthly:    (p) => apiFetch(`/data/charts/monthly${toQS(p)}`),
  getChartFunnel:     (p) => apiFetch(`/data/charts/funnel${toQS(p)}`),
  getChartRejections: (p) => apiFetch(`/data/charts/rejections${toQS(p)}`),
  getRejectionsDetail:(p) => apiFetch(`/data/rejections-detail${toQS(p)}`),
  searchProducts:     (q) => apiFetch(`/data/search/products?q=${encodeURIComponent(q)}`),
  getAlerts:          (p) => apiFetch(`/data/alerts${toQS(p)}`),
  getComparativa:     (p) => apiFetch(`/data/comparativa${toQS(p)}`),

  // Exports PDF
  exportPdfUrl: (type, params) => `${API_BASE}/exports/${type}${toQS(params)}`,
  sendExportMail: (data) => apiFetch('/exports/send', { method: 'POST', body: JSON.stringify(data) }),

  // Mail
  addMailAccount:    (user, password) => apiFetch('/mail/accounts', { method: 'POST', body: JSON.stringify({ user, password }) }),
  deleteMailAccount: (email) => apiFetch(`/mail/accounts/${encodeURIComponent(email)}`, { method: 'DELETE' }),
  // manual=true: lo pidió una persona con el botón → no aplica el cooldown de 10 min
  syncMail: (manual) => apiFetch(`/mail/sync${manual ? '?manual=1' : ''}`, { method: 'POST' }),
  getLastSync: () => apiFetch('/mail/last-sync'),

  // Notifications
  getNotificationRules: () => apiFetch('/notifications/rules'),
  getNotificationsInbox:  ()    => apiFetch('/notifications/inbox'),
  ackAssignedQuote: (quoteId)   => apiFetch('/notifications/ack-assigned', { method: 'POST', body: JSON.stringify({ quoteId }) }),
  ackMention: (noteId)          => apiFetch('/notifications/ack-mention', { method: 'POST', body: JSON.stringify({ noteId }) }),
  testWeeklyReport: ()          => apiFetch('/notifications/weekly-report/test', { method: 'POST' }),

  // Articles
  getArticles:       (p) => apiFetch(`/articles${toQS(p)}`),
  searchArticles:    (q) => apiFetch(`/articles/search${toQS({ q })}`),
  getArticleByCode:  (code) => apiFetch(`/articles/${encodeURIComponent(code)}`),
  getArticleMeta:    () => apiFetch('/articles/meta'),
  createArticle:     (data) => apiFetch('/articles', { method: 'POST', body: JSON.stringify(data) }),
  updateArticle:     (code, data) => apiFetch(`/articles/${encodeURIComponent(code)}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteArticle:     (code) => apiFetch(`/articles/${encodeURIComponent(code)}`, { method: 'DELETE' }),
  previewArticleXLS: (file) => {
    const token = CrmAuth.getToken();
    const fd = new FormData(); fd.append('file', file);
    return fetch(`${API_BASE}/articles/preview`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
    }).then(r => r.ok ? r.json() : r.json().then(b => Promise.reject(new Error(b.error || `Error ${r.status}`))));
  },
  syncArticles: (token, deleteCodes) => apiFetch('/articles/sync', {
    method: 'POST', body: JSON.stringify({ token, deleteCodes }),
  }),
  deleteAllArticles: () => apiFetch('/articles/all', { method: 'DELETE' }),

  // Attachments
  deleteAttachment: (id) => apiFetch(`/attachments/${id}`, { method: 'DELETE' }),

  // Email sending accounts
  getSendAccounts:  ()  => apiFetch('/quotes/send-accounts'),

  // ── Feedback / Foro ────────────────────────────────────────────────────────
  getFeedbackMeta:    ()           => apiFetch('/feedback/meta'),
  markFeedbackSeen:   ()           => apiFetch('/feedback/mark-seen', { method: 'POST' }),
  getFeedbackPosts:   ()           => apiFetch('/feedback'),
  createFeedbackPost: (data)       => apiFetch('/feedback', { method: 'POST', body: JSON.stringify(data) }),
  respondFeedback:    (id, body, status, draft) => apiFetch(`/feedback/${id}/respond`, { method: 'POST', body: JSON.stringify({ body, status, draft: !!draft }) }),
  getFeedbackDrafts:  () => apiFetch('/feedback/drafts'),
  publishFeedbackDrafts: () => apiFetch('/feedback/publish-drafts', { method: 'POST' }),
  commentFeedback:    (id, body)         => apiFetch(`/feedback/${id}/comment`, { method: 'POST', body: JSON.stringify({ body }) }),
  setFeedbackStatus:  (id, status) => apiFetch(`/feedback/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  voteFeedback:         (id)           => apiFetch(`/feedback/${id}/vote`, { method: 'POST' }),

  // ── Developer settings ────────────────────────────────────────────────────
  getFeedbackNotifyUsers: ()      => apiFetch('/settings/feedback-notify-users'),
  saveFeedbackNotifyUsers: (ids)  => apiFetch('/settings/feedback-notify-users', { method: 'PUT', body: JSON.stringify({ ids }) }),
  getFeedbackPost:    (id)         => apiFetch(`/feedback/${id}`),
  editFeedbackPost:   (id, data)   => apiFetch(`/feedback/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteFeedbackPost: (id)         => apiFetch(`/feedback/${id}`, { method: 'DELETE' }),

  uploadFeedbackImage: (file) => {
    const fd = new FormData();
    fd.append('image', file);
    const token = localStorage.getItem('crm_token');
    return fetch('/api/feedback/upload-image', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    }).then(r => r.json());
  },

  // Email templates
  getEmailTemplates: () => apiFetch('/quotes/email-templates'),
  saveEmailTemplates: (data) => apiFetch('/quotes/email-templates', { method: 'PUT', body: JSON.stringify(data) }),
  sendQuoteEmail: (quoteId, data) => apiFetch(`/quotes/${quoteId}/send-email`, { method: 'POST', body: JSON.stringify(data) }),
  sendReminder: (quoteId, data) => apiFetch(`/quotes/${quoteId}/send-reminder`, { method: 'POST', body: JSON.stringify(data) }),

  // Upload attachments
  uploadAttachments: (quoteId, files) => {
    const token = CrmAuth.getToken();
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    return fetch(`${API_BASE}/quotes/${quoteId}/attachments`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    }).then(r => r.ok ? r.json() : r.json().then(b => Promise.reject(new Error(b.error || `Error ${r.status}`))));
  },
};

// ─── Load all data from API (replaces hardcoded data) ───
async function loadAllData() {
  try {
    // Cargar últimos 12 meses — reduce payload en DBs grandes
    const since = new Date(Date.now() - 365 * 86400 * 1000).toISOString().split('T')[0];
    const [quotes, clients, users, stages, activity] = await Promise.all([
      CrmApi.getQuotes({ since }),
      CrmApi.getClients(),
      CrmApi.getUsers(),
      CrmApi.getStages(),
      CrmApi.getActivity(),
    ]);

    // Map users to frontend format
    const roleMap = { DEVELOPER: 'Desarrollador', ADMIN: 'Administrador', VENDEDOR: 'Vendedor', LOGISTICA: 'Logística' };
    const mappedUsers = users.map(u => ({
      id: u.id,
      name: u.name,
      email: u.email,
      role: roleMap[u.role] || u.role,
      zone: u.zone || '—',
      active: u.active !== false,
    }));

    // Map clients to frontend format
    const mappedClients = clients.map(c => ({
      id: c.id,
      code: c.code,
      name: c.name,
      cuit: c.cuit || '',
      city: c.city || '',
      prov: c.province || '',
      zone: c.zone || '',
      activity: c.activity || '',
      seller: c.defaultSellerId || '',
      sellerName: c.defaultSeller?.name || '',
      legacySellerName: c.legacySellerName || '',
      email: c.emailPrimary || c.email || '',
      phone: c.phone || '',
      address: c.address || '',
    }));

    return {
      users: mappedUsers,
      clients: mappedClients,
      quotes,
      stagesF1: stages.f1,
      activity,
    };
  } catch (err) {
    // El modo mantenimiento se maneja aparte (pantalla dedicada en AppRoot) —
    // no lo tratamos como un simple "no se pudo conectar".
    if (err.message === 'maintenance') throw err;
    console.warn('⚠️ Could not load API data, using defaults:', err.message);
    return null;
  }
}

// Expose to other scripts
Object.assign(window, { CrmAuth, CrmApi, apiFetch, loadAllData });
